import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useStudioAuth } from "./StudioAuthContext";

const ICE = [{ urls: ["stun:stun.l.google.com:19302", "stun:global.stun.twilio.com:3478"] }];
const ChaserStreamContext = createContext(null);

export const ChaserStreamProvider = ({ children }) => {
  const { request } = useStudioAuth();
  const [stream, setStream] = useState(null);
  const [session, setSession] = useState(null);
  const [state, setState] = useState("idle");
  const pcRef = useRef(null);
  const timerRef = useRef(null);
  const sinceRef = useRef(null);

  const disconnect = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
    pcRef.current?.close();
    pcRef.current = null;
    sinceRef.current = null;
    setStream(null);
    setSession(null);
    setState("idle");
  }, []);

  const connect = useCallback(async (target) => {
    disconnect();
    setSession(target);
    setState("connecting");

    const pc = new RTCPeerConnection({ iceServers: ICE });
    pcRef.current = pc;
    pc.ontrack = (event) => setStream(event.streams[0]);
    pc.onicecandidate = (event) => {
      if (!event.candidate) return;
      request("/chase/signal", {
        method: "POST",
        body: JSON.stringify({ session_id: target.id, role: "studio", payload: { candidate: event.candidate } }),
      }).catch(() => {});
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "connected") setState("live");
      if (pc.connectionState === "failed") setState("failed");
    };

    const pump = async () => {
      try {
        const query = `/chase/signal?session_id=${target.id}&role=studio${sinceRef.current ? `&since=${encodeURIComponent(sinceRef.current)}` : ""}`;
        const response = await request(query);
        if (!response.ok) return;
        for (const message of await response.json()) {
          sinceRef.current = message.created_at;
          if (message.payload?.sdp && !pc.currentRemoteDescription) {
            await pc.setRemoteDescription(message.payload.sdp);
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            await request("/chase/signal", {
              method: "POST",
              body: JSON.stringify({ session_id: target.id, role: "studio", payload: { sdp: pc.localDescription } }),
            });
          } else if (message.payload?.candidate) {
            await pc.addIceCandidate(message.payload.candidate).catch(() => {});
          }
        }
      } catch (_) {}
    };

    await pump();
    timerRef.current = setInterval(pump, 1500);
  }, [request, disconnect]);

  const value = useMemo(() => ({ stream, session, state, connect, disconnect }), [stream, session, state, connect, disconnect]);
  return <ChaserStreamContext.Provider value={value}>{children}</ChaserStreamContext.Provider>;
};

export const useChaserStream = () => useContext(ChaserStreamContext) || { stream: null, session: null, state: "idle" };
